import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import enTranslations from '../../extractedTranslations/en/translation.json';
import deTranslations from '../../extractedTranslations/de/translation.json';
import SessionStorageManager from './SessionStorageManager';

export interface II18nProps {
  t: (text: string) => any;
  i18n: any;
  tReady: boolean;
}

class I18nService {
  private onLangChangeFns = [];

  private i18n;

  public init() {
    const resources = {
      de: {
        translation: deTranslations
      },
      en: {
        translation: enTranslations
      }
    };

    let applicationSettings = SessionStorageManager.getApplicationSettings();

    i18n
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        lng: applicationSettings?.language || 'de',
        fallbackLng: 'en',
        resources,

        keySeparator: false,
        nsSeparator: false,

        interpolation: {
          escapeValue: false
        }
      });

    // attach events
    i18n.on('languageChanged', (lng) => {
      this.onLangChangeFns.forEach((fn) => {
        fn(lng);
      });

      SessionStorageManager.storeApplicationSettings({
        language: lng
      });
    });

    this.i18n = i18n;
    return this.i18n;
  }

  /**
   * Register function to be executed when the lang changes
   *
   * @param cb
   */
  public onLangChange(cb) {
    // add the callback to the list
    this.onLangChangeFns.push(cb);

    // return removal function
    return () => {
      let index = this.onLangChangeFns.indexOf(cb);
      if (index > -1) {
        this.onLangChangeFns.splice(index, 1);
      }
    };
  }

  /**
   * Translate text
   *
   * @param text
   */
  public t(text: string, params?: any) {
    return this.i18n.t(text, params);
  }

  /**
   * Returns the current active language
   */
  public getActiveLang(): string {
    return this.i18n.language;
  }

  /**
   * Change the current active language
   *
   * @param lang
   */
  public async changeLang(lang: 'en' | 'de') {
    try {
      await this.i18n.changeLanguage(lang);
    } catch (e) {
      throw new Error('Error while trying the change the language');
    }
  }
}

export default new I18nService();
